import React, { FC, useMemo } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { getFlagUrl } from '../../utils/getFlagUrl';

type ResultsTableProps = {
    values: string[][];
    title?: string;
    isTeam?: boolean; // False = Madison (Dvojice), True = Team Sprint (3-5 jezdců)
};

export const MultipleRidersTable: FC<ResultsTableProps> = ({ values, title, isTeam = false }) => {
    const [headers, ...rows] = values;

    // Dynamické dohledání indexů sloupců podle názvu v Google Sheetu
    const countryColIndex = headers.findIndex((h) => h.toLowerCase() === 'country');
    const qColIndex = headers.findIndex((h) => h.toLowerCase() === 'q');
    const pointsIndex = headers.findIndex((h) => h.toLowerCase().includes('points'));
    const timeIndex = headers.findIndex((h) => h.toLowerCase().includes('time'));
    
    // Najde index sloupce pro BIB / Team ID (obvykle index 1)
    const bibIndex = headers.findIndex((h) => 
        h.toLowerCase() === 'bib' || 
        h.toLowerCase().includes('team bib') || 
        h.toLowerCase().includes('code')
    );

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const isAbandoned = (value?: string) => value === 'ABD' || value === 'DNF' || value === 'DNS';
    const isQualified = (value?: string) => value === 'Q';

    // Seskupení řádků do týmů podle hodnoty ve sloupci BIB
    const groups = useMemo(() => {
        const result: string[][][] = [];
        let current: string[][] = [];
        const targetBibIndex = bibIndex !== -1 ? bibIndex : 1;

        rows.forEach((row, idx) => {
            if (idx > 0 && row[targetBibIndex] !== rows[idx - 1][targetBibIndex]) {
                if (current.length > 0) result.push(current);
                current = [];
            }
            current.push(row);
        });
        if (current.length > 0) result.push(current);
        return result;
    }, [rows, bibIndex]);

    // Sloupce, které na desktopu dostanou rowSpan (sloučení)
    const mergeIndices = useMemo(() => {
        const indices = [0]; // Rank (index 0) slučujeme vždy
        
        if (!isTeam) {
            // MADISON: Slučujeme BIB a celkové body
            if (bibIndex !== -1) indices.push(bibIndex);
            if (pointsIndex !== -1) indices.push(pointsIndex);
        } else {
            // TEAM SPRINT: Slučujeme časové sloupce (např. Time, případně i následující split)
            if (timeIndex !== -1) {
                indices.push(timeIndex);
                if (headers[timeIndex + 1] && headers[timeIndex + 1].toLowerCase().includes('time')) {
                    indices.push(timeIndex + 1);
                }
            }
        }
        return indices;
    }, [isTeam, bibIndex, pointsIndex, timeIndex, headers]);

    // Filtrované záhlaví pro desktop: u Team Sprintu schováme sloupec BIB
    const displayHeaders = useMemo(() => {
        if (isTeam && bibIndex !== -1) {
            return headers.filter((_, i) => i !== bibIndex);
        }
        return headers;
    }, [headers, isTeam, bibIndex]);

    // =========================================================
    // 📱 MOBILNÍ ZOBRAZENÍ (Komplet opravené pro Madison i Týmy)
    // =========================================================
    if (isMobile) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, marginTop: 2, marginBottom: 2 }}>
                {title && <Typography variant="h6" fontWeight={700} sx={{ px: 1 }}>{title}</Typography>}
                {groups.map((group, index) => {
                    const firstRider = group[0];
                    if (!firstRider) return null;

                    const rank = firstRider[0];
                    const bib = bibIndex !== -1 ? firstRider[bibIndex] : '';
                    
                    // Hodnota pro pravý roh (Body pro Madison, Čas pro Team Sprint)
                    const finalValue = !isTeam && pointsIndex !== -1 
                        ? `${firstRider[pointsIndex]} pts` 
                        : (timeIndex !== -1 ? firstRider[timeIndex] : '');

                    return (
                        <Paper key={index} sx={{ p: 2, borderRadius: 3, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                {/* Levý sloupec: Rank & BIB (pouze u Madisonu) */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 35 }}>
                                    <Typography fontWeight={700}>
                                        {rank === 'DNF' || rank === 'DNS' || rank === 'ABD' ? '' : '#'} {rank}
                                    </Typography>
                                    {!isTeam && bib && (
                                        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mt: 1 }}>
                                            {bib}
                                        </Typography>
                                    )}
                                </Box>

                                {/* Střední sloupec: Seznam jezdců v týmu */}
                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {group.map((rider, rIdx) => {
                                        const country = rider[countryColIndex];
                                        return (
                                            <Box key={rIdx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Box>
                                                    <Typography fontWeight={600} sx={{ lineHeight: 1.1 }}>
                                                        {rider[2] ?? ''}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', pl: 0.5 }}>
                                                        {rider[3] ?? ''}
                                                    </Typography>
                                                </Box>
                                                {country && (
                                                    <img
                                                        src={getFlagUrl(country)}
                                                        alt={country}
                                                        style={{ width: '22px', height: '15px', objectFit: 'cover', borderRadius: '2px' }}
                                                    />
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>

                                {/* Pravý sloupec: Výsledný čas nebo body */}
                                <Box sx={{ minWidth: 65, textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                    <Typography fontWeight={700} variant="body2">
                                        {finalValue}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    );
                })}
            </Box>
        );
    }

    // =========================================================
    // 🖥️ DESKTOP ZOBRAZENÍ (S tlustou čárou přes celou šířku)
    // =========================================================
    return (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3, marginTop: 2, marginBottom: 2, overflowX: 'auto' }}>
            {title && (
                <Box sx={{ px: 2, py: 1.5, backgroundColor: 'grey.900', color: 'common.white', textAlign: 'center' }}>
                    <Typography fontWeight={600}>{title}</Typography>
                </Box>
            )}
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                        {displayHeaders.map((header, index) => (
                            <TableCell
                                key={index}
                                align="center"
                                sx={{
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    fontSize: '0.75rem',
                                    backgroundColor: 'grey.900',
                                    color: 'common.white',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {header}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {groups.map((group, groupIndex) =>
                        group.map((row, rowIndex) => {
                            const isLastInGroup = rowIndex === group.length - 1;
                            const isLastGroupInTable = groupIndex === groups.length - 1;

                            // Definice spodního ohraničení pro buňky řádku
                            const cellBorderBottom = isLastInGroup && !isLastGroupInTable
                                ? '2px solid rgba(0, 0, 0, 0.87)' // Tlustá čára na konci týmu
                                : '1px solid rgba(224, 224, 224, 1)'; // Jemná linka uvnitř týmu

                            return (
                                <TableRow key={`${groupIndex}-${rowIndex}`}>
                                    {headers.map((_, cellIndex) => {
                                        // V Team Sprintu kompletně ignorujeme a nerenderujeme sloupec BIB
                                        if (isTeam && cellIndex === bibIndex) return null;

                                        const cellValue = row[cellIndex];
                                        const abandoned = isAbandoned(cellValue);
                                        const qualified = cellIndex === qColIndex && isQualified(cellValue);

                                        // Pokud je sloupec určen ke sloučení a nejsme na 1. řádku týmu, buňku přeskočíme
                                        if (mergeIndices.includes(cellIndex) && rowIndex > 0) return null;

                                        return (
                                            <TableCell
                                                key={cellIndex}
                                                align="center"
                                                rowSpan={mergeIndices.includes(cellIndex) ? group.length : 1}
                                                sx={{
                                                    verticalAlign: 'middle',
                                                    fontWeight: cellIndex === 0 || abandoned ? 600 : 400,
                                                    borderBottom: cellBorderBottom, // Zajištění čáry přes celou šířku
                                                }}
                                            >
                                                {qualified ? (
                                                    <span className="qualified-badge">Q</span>
                                                ) : cellIndex === countryColIndex && cellValue ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <img src={getFlagUrl(cellValue)} alt={cellValue} className="flag-icon" />
                                                    </Box>
                                                ) : (
                                                    cellValue ?? '—'
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};